import React from 'react';
import { pull, uniq, map, debounce } from 'lodash';
import FieldsSelectorHelper from './FieldsSelectorHelper';

class FieldSelector extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            settings: props.settings,
            current: props.current,
            initial: Object.assign({}, props.current),
            dragOverField: null,
            expandedGroups: new Set(['overview']),
            searchTerm: '',
        };

        this.draggingField = null;
        this.duplicateLabels = new Map(); // label → array of groups

        this.changeFields = this.changeFields.bind(this);
        this.applyFilter = this.applyFilter.bind(this);
        this.selectAllFields = this.selectAllFields.bind(this);
        this.unselectAllFields = this.unselectAllFields.bind(this);
        this.toggleGroup = this.toggleGroup.bind(this);
        this.toggleGroupAll = this.toggleGroupAll.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.toggleAllFields = this.toggleAllFields.bind(this);
        this.debouncedSearch = debounce(this.handleSearch, 150);
    }

    componentDidMount() {
        this.detectDuplicateLabels();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.settings !== this.props.settings) {
            this.setState({ settings: this.props.settings }, this.detectDuplicateLabels);
        }
    }

        // ─── Detect duplicate labels across groups ─────────────────────────
    detectDuplicateLabels() {
        const labelMap = new Map();

        const allFields = FieldsSelectorHelper.pickFields(this.state.settings || this.props.settings || []);

        allFields.forEach(field => {
            if (field.expander === true || field.required === true) return;

            const rawLabel = (field.headerAlt || field.Header || field.id || '').trim();
            if (!rawLabel) return;

            const group = field.sourceGroup || 'overview';

            if (!labelMap.has(rawLabel)) {
                labelMap.set(rawLabel, []);
            }
            labelMap.get(rawLabel).push(group);
        });

        const duplicates = new Map();
        labelMap.forEach((groups, label) => {
            if (new Set(groups).size > 1) {
                duplicates.set(label, groups);
            }
        });

        this.duplicateLabels = duplicates;
    }

    // ─── Get Field Label (Robust version) ──────────────────────────────
    getFieldLabel(id) {
        if (!id) return '';

        const field = this.state.settings  && this.state.settings.find(f => f.id === id) 
                || this.props.settings && this.props.settings.find(f => f.id === id);

        if (!field) {
            console.warn(`Field not found: ${id}`);
            return id;
        }

        let label = (field.headerAlt || field.Header || field.id || id).trim();

        // Add group name in braces only if this label is duplicated
        if (this.duplicateLabels.has(label)) {
            const groupName = field.sourceGroup || 'overview';
            const displayGroup = groupName === 'overview' ? 'Overview' : groupName;
            label = `${label} (${displayGroup})`;
        }

        return label;
    }

    // ─── Field Change Logic ─────────────────────────────────────────────

    changeFields(e) {
        const { name, value } = e.target;
        let currentFields = this.state.current.columns.slice(0);
        const allFields = this.state.settings;
        const settingFields = this.props.settings;

        const isCatalog = !!e.target.closest('.catalog-report-selector');
        const totalField = settingFields.find(field => field.id === 'overview');

        let fieldsToChange = [value];

        for (let field of allFields) {
            if (field.sourceGroup && field.sourceGroup === value) {
                fieldsToChange.push(field.id);
            } else if (
                field.sourceGroup == null &&
                value === 'overview' &&
                (!totalField || !totalField.isTotalExpanded) &&
                !field.required &&
                isCatalog
            ) {
                fieldsToChange.push(field.id);
            }
        }

        if (currentFields.includes(value)) {
            for (let f of fieldsToChange) pull(currentFields, f);
        } else {
            for (let f of fieldsToChange) {
                if (!currentFields.includes(f)) currentFields.push(f);
            }
        }

        const newCurrent = { ...this.state.current, [name]: currentFields };

        this.setState({ current: newCurrent }, () => {
            this.notifyParent(newCurrent);
        });
    }

    // ─── Group & Selection Logic ───────────────────────────────────────

    toggleGroupAll(groupKey) {
        const fieldsInGroup = this.getFieldsInGroup(groupKey);
        if (fieldsInGroup.length === 0) return;

        const currentCols = this.state.current.columns;
        const currentlyAllSelected = fieldsInGroup.every(f => currentCols.includes(f.id));

        let newColumns = [...currentCols];

        if (currentlyAllSelected) {
            // Unselect all in group
            for (let field of fieldsInGroup) {
                pull(newColumns, field.id);
            }
        } else {
            // Select all in group
            for (let field of fieldsInGroup) {
                if (!newColumns.includes(field.id)) {
                    newColumns.push(field.id);
                }
            }
        }

        const newCurrent = { ...this.state.current, columns: newColumns };

        this.setState({ current: newCurrent }, () => {
            this.notifyParent(newCurrent);
        });
    }

    toggleAllFields() {
        const allSelectable = FieldsSelectorHelper.pickFields(this.state.settings)
            .filter(f => f.expander !== true && f.required !== true && f.id !== 'overview')
            .map(f => f.id);

        const currentCols = this.state.current.columns;
        const isAllSelected = allSelectable.length > 0 && allSelectable.every(id => currentCols.includes(id));

        let newColumns = [...currentCols];

        if (isAllSelected) {
            newColumns = newColumns.filter(id => !allSelectable.includes(id));
        } else {
            newColumns = [...new Set([...newColumns, ...allSelectable])];
        }

        const newCurrent = { ...this.state.current, columns: newColumns };

        this.setState({ current: newCurrent }, () => {
            this.notifyParent(newCurrent);
        });
    }

    notifyParent(current) {
        const columns = current.columns.filter(id => !this.getGroupHeaderIds().has(id));
        
        if (this.props.onUpdate) {
            this.props.onUpdate({ ...current, columns });
        }
    }

    resetFilter() {
        this.toggleAllFields();
    }

    applyFilter() {
        const filter = this.state.current;
        const columns = filter.columns.filter(id => !this.getGroupHeaderIds().has(id));

        if (this.props.onUpdate) {
            this.props.onUpdate({ ...filter, columns });
        }
    }

    // ─── Helpers ───────────────────────────────────────────────────────

    getFieldsInGroup(groupKey) {
        return FieldsSelectorHelper.pickFields(this.state.settings)
            .filter(item => {
                if (item.expander === true || item.required === true) return false;
                if (item.id === 'overview') return false;

                if (groupKey === 'overview' || groupKey == null) {
                    return item.sourceGroup == null;
                }
                return item.sourceGroup === groupKey;
            });
    }

    getGroupHeaderIds() {
        let groupSet = new Set(
            this.state.settings.map(f => f.sourceGroup).filter(Boolean)
        );
        groupSet.add('overview');
        return groupSet;
    }

    getSelectedColumns() {
        const groupHeaderIds = this.getGroupHeaderIds();
        const selectableIds = new Set(
            FieldsSelectorHelper.pickFields(this.state.settings)
                .filter(f => f.expander !== true && f.required !== true && f.id !== 'overview' && !groupHeaderIds.has(f.id))
                .map(f => f.id)
        );
        return this.state.current.columns.filter(id => selectableIds.has(id));
    }

    getGroupTitle(groupKey) {
        if (!groupKey || groupKey === 'overview') {
            const overviewField = this.state.settings.find(f => f.id === 'overview');
            return (overviewField && overviewField.headerAlt) || (overviewField && overviewField.Header) || 'Overview';
        }
        return groupKey;
    }

    toggleGroup(groupKey) {
        const newExpanded = new Set(this.state.expandedGroups);
        if (newExpanded.has(groupKey)) {
            newExpanded.delete(groupKey);
        } else {
            newExpanded.add(groupKey);
        }
        this.setState({ expandedGroups: newExpanded });
    }

    handleSearch(value) {
        if (typeof value === 'string') {
            this.setState({ 
                searchTerm: value.toLowerCase().trim() 
            });
        }
    }

    // ─── Drag & Drop ───────────────────────────────────────────────────

    onDragStart(e, fieldId) {
        this.draggingField = fieldId;
        e.dataTransfer.effectAllowed = 'move';
    }

    onDragOver(e, fieldId) {
        e.preventDefault();
        if (this.state.dragOverField !== fieldId) {
            this.setState({ dragOverField: fieldId });
        }
    }

    onDragEnd() {
        this.draggingField = null;
        this.setState({ dragOverField: null });
    }

    onDrop(e, targetId) {
        e.preventDefault();
        const dragging = this.draggingField;
        this.draggingField = null;
        this.setState({ dragOverField: null });

        if (!dragging || dragging === targetId) return;

        const columns = this.state.current.columns.slice(0);
        const fromIdx = columns.indexOf(dragging);
        const toIdx = columns.indexOf(targetId);
        if (fromIdx === -1 || toIdx === -1) return;

        columns.splice(fromIdx, 1);
        columns.splice(toIdx, 0, dragging);

        this.setState({ current: { ...this.state.current, columns } });
    }

    // ─── Render ────────────────────────────────────────────────────────

    selectAllFields() {
        const allSelectable = FieldsSelectorHelper.pickFields(this.state.settings)
            .filter(f => f.expander !== true && f.required !== true && f.id !== 'overview')
            .map(f => f.id);

        const currentCols = this.state.current.columns;
        const newColumns = [...new Set([...currentCols, ...allSelectable])];

        this.setState({
            current: { ...this.state.current, columns: newColumns }
        });
    }

    unselectAllFields() {
        const allSelectable = FieldsSelectorHelper.pickFields(this.state.settings)
            .filter(f => f.expander !== true && f.required !== true && f.id !== 'overview')
            .map(f => f.id);

        const currentCols = this.state.current.columns;
        const newColumns = currentCols.filter(id => !allSelectable.includes(id));

        this.setState({
            current: { ...this.state.current, columns: newColumns }
        });
    }

    render() {
        const { sortableColumns = false } = this.props;
        const { searchTerm, expandedGroups } = this.state;

        const allGroups = uniq(map(this.state.settings, field => field.sourceGroup))
            .filter(g => g !== 'overview' && g != null);

        const selectedCols = this.getSelectedColumns();
        const { dragOverField } = this.state;
        const extraClassName = this.props.extraClassName || '';

        const filteredGroups = allGroups.map(groupKey => {
            let fieldsInGroup = this.getFieldsInGroup(groupKey)
                .filter(item => {
                    if (!searchTerm) return true;
                    const label = (item.headerAlt || item.Header || item.id || '').toLowerCase();
                    return label.includes(searchTerm);
                });

            const firstField = fieldsInGroup.length > 0 ? fieldsInGroup[0] : null;
            const displayFields = firstField ? fieldsInGroup.slice(1) : [];

            return { 
                groupKey, 
                fields: displayFields,
                firstField 
            };
        }).filter(({ firstField }) => firstField !== null);

        const overviewGroup = {
            groupKey: 'overview',
            fields: this.getFieldsInGroup('overview')
                .filter(item => {
                    if (!searchTerm) return true;
                    const label = (item.headerAlt || item.Header || item.id || '').toLowerCase();
                    return label.includes(searchTerm);
                })
        };

        const showOverview = overviewGroup.fields.length > 0;

        return (
            <div className={`fields-selector-root ${extraClassName}`}>
                <div className="manage-fields-body">
                    <div className="manage-fields-body-item">
                        <h4>Fields</h4>
                        <div className="manage-fields-header">
                            <input
                                type="text"
                                placeholder="Search fields..."
                                className="field-search-input"
                                onChange={(e) => this.debouncedSearch(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="manage-fields-body-item">
                    </div>
                </div>
                <div className="manage-fields-header">
                    <div className="links-holder">
                        <div className='link-holder-select-action'>
                            <a className="reset" onClick={this.selectAllFields}>Select All</a>
                            <span>/</span>
                            <a className="reset" onClick={this.unselectAllFields}>Clear All</a>
                        </div>
                        <a className="apply" onClick={this.applyFilter}>Apply</a>
                        
                    </div>
                </div>
                <div className="manage-fields-body">
                    <div className="manage-fields-body-item">
                        <div className="manage-fields-selection">
                            {/* Overview Group */}
                            {showOverview && (
                                <div className="group-container">
                                    <div className="group-header">
                                        <span 
                                            className="toggle-icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                this.toggleGroup('overview');
                                            }}
                                        >
                                            <span className={(expandedGroups.has('overview') || searchTerm !== '') ? 'fa fa-caret-down' : 'fa fa-caret-right'}></span>
                                        </span>
                                        <div className="input-holder">
                                            <input
                                                type="checkbox"
                                                id="group-overview"
                                                checked={overviewGroup.fields.every(f => this.state.current.columns.includes(f.id))}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    this.toggleGroupAll('overview', overviewGroup.fields.every(f => this.state.current.columns.includes(f.id)));
                                                }}
                                                className="input-checkbox visually-hidden"
                                            />
                                            <label className="group-title default-checkbox" htmlFor="group-overview">
                                                {this.getGroupTitle('overview')}
                                            </label>
                                        </div>
                                    </div>

                                    {(expandedGroups.has('overview') || searchTerm !== '') && (
                                        <div className="group-fields">
                                            {overviewGroup.fields.map((item) => (
                                                <div key={item.id} className="manage-fields-item">
                                                    <div className="input-holder">
                                                        <input
                                                            type="checkbox"
                                                            name="columns"
                                                            value={item.id}
                                                            onChange={this.changeFields}
                                                            checked={this.state.current.columns.includes(item.id)}
                                                            id={item.id}
                                                            className="input-checkbox visually-hidden"
                                                        />
                                                        <label className="default-checkbox" htmlFor={item.id}>
                                                            {item.headerAlt || item.Header || item.id}
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Other Groups */}
                            {filteredGroups.map(({ groupKey, fields: groupFields, firstField }) => {
                                const isExpanded = expandedGroups.has(groupKey) || searchTerm !== '';
                                
                                const groupLabel = firstField 
                                    ? (firstField.headerAlt || firstField.Header || groupKey)
                                    : this.getGroupTitle(groupKey);

                                // Calculate current group selection status
                                const allFieldsInGroup = firstField ? [firstField, ...groupFields] : groupFields;
                                const isGroupSelected = allFieldsInGroup.length > 0 && 
                                    allFieldsInGroup.every(f => this.state.current.columns.includes(f.id));

                                return (
                                    <div key={groupKey} className="group-container">
                                        <div className="group-header">
                                            <span 
                                                className="toggle-icon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    this.toggleGroup(groupKey);
                                                }}
                                            >
                                                <span className={isExpanded ? 'fa fa-caret-down' : 'fa fa-caret-right'}></span>
                                            </span>
                                            
                                            <div className="input-holder">
                                                <input
                                                    type="checkbox"
                                                    id={`group-${groupKey}`}
                                                    checked={isGroupSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        this.toggleGroupAll(groupKey, isGroupSelected);
                                                    }}
                                                    className="input-checkbox visually-hidden"
                                                />
                                                <label 
                                                    className="group-title default-checkbox" 
                                                    htmlFor={`group-${groupKey}`}
                                                >
                                                    {groupLabel}
                                                </label>
                                            </div>
                                        </div>

                                        {isExpanded && groupFields.length > 0 && (
                                            <div className="group-fields">
                                                {groupFields.map((item) => (
                                                    <div key={item.id} className="manage-fields-item">
                                                        <div className="input-holder">
                                                            <input
                                                                type="checkbox"
                                                                name="columns"
                                                                value={item.id}
                                                                onChange={this.changeFields}
                                                                checked={this.state.current.columns.includes(item.id)}
                                                                id={item.id}
                                                                className="input-checkbox visually-hidden"
                                                            />
                                                            <label className="default-checkbox" htmlFor={item.id}>
                                                                {item.headerAlt || item.Header || item.id}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {filteredGroups.length === 0 && !showOverview && searchTerm && (
                                <div className="no-results">No fields found matching "{searchTerm}"</div>
                            )}
                        </div>
                    </div>

                    {/* Right Panel - Sortable Order */}
                    <div className="manage-fields-body-item">
                        <div className="manage-fields-header">
                            <h4 className="columns-ordering-title">Columns Ordering</h4>
                        </div>
                        {sortableColumns && (
                            <div className="manage-fields-order global-filter-preview-list">
                                {selectedCols.length === 0 && (
                                    <div className="order-empty">No columns selected</div>
                                )}
                                {selectedCols.map((colId) => {
                                    const isDragOver = dragOverField === colId && this.draggingField !== colId;
                                    return (
                                        <div
                                            key={colId}
                                            className={`order-item${isDragOver ? ' drag-over' : ''}`}
                                            draggable
                                            onDragStart={(e) => this.onDragStart(e, colId)}
                                            onDragOver={(e) => this.onDragOver(e, colId)}
                                            onDragEnd={() => this.onDragEnd()}
                                            onDrop={(e) => this.onDrop(e, colId)}
                                        >
                                            <span className="drag-handle" aria-hidden="true">⠿</span>
                                            <span className="order-item-label">
                                                {this.getFieldLabel(colId)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>                   
                </div>
            </div>
        );
    }
}

export default FieldSelector;